// Şub 27, 2026 — DPD Kurye Eşleştirme
// DPD route_data'daki courier_name -> HR employee mapping (fuzzy + manuel)
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { UserCheck, RefreshCw, Loader2, CheckCircle2, XCircle, Sparkles, Link as LinkIcon, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { toast } from 'sonner';

export default function DPDCourierMapping() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [data, setData] = useState({ rows: [], total_couriers: 0, mapped: 0, unmapped: 0 });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoMinScore, setAutoMinScore] = useState(75);
  const [autoLoading, setAutoLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState({});

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const r = await api.get(`/dpd/couriers/unmapped?firm_id=${firmId}`);
      setData(r.data?.data);
      // Load ALL firm users (employees + admins) for the searchable dropdown
      const e = await api.get(`/admin/users?firm_id=${firmId}&limit=5000`);
      const list = (e.data?.data || []).map((u) => ({
        ...u,
        _searchable_name: (u.full_name || `${u.first_name || ''} ${u.last_name || ''}`).trim(),
      }));
      setEmployees(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kurye listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyMapping = async (courierName, employeeId, type = 'manual') => {
    try {
      await api.post('/dpd/couriers/mappings', {
        firm_id: firmId,
        courier_name: courierName,
        employee_id: employeeId,
        mapping_type: type,
      });
      toast.success(`${courierName} eşleştirildi`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eşleştirme başarısız');
    }
  };

  const removeMapping = async (courierName) => {
    try {
      await api.delete(`/dpd/couriers/mappings/${encodeURIComponent(courierName)}?firm_id=${firmId}`);
      toast.success(`${courierName} eşleştirmesi kaldırıldı`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaldırma başarısız');
    }
  };

  const runAutoMap = async () => {
    setAutoLoading(true);
    try {
      const r = await api.post(`/dpd/couriers/auto-map?firm_id=${firmId}&min_score=${autoMinScore}`);
      const applied = r.data?.data?.applied || 0;
      toast.success(`${applied} kurye otomatik eşleştirildi (skor ≥ ${autoMinScore})`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Otomatik eşleştirme başarısız');
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dpd-courier-mapping-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-teal-600" />
            {t('dpd.courierMapping.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.courierMapping.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="cm-firm-select"><SelectValue placeholder={t('dpd.common.firm')} /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <span className="text-xs whitespace-nowrap">{t('dpd.courierMapping.autoMinLabel')}</span>
              <Input type="number" min={50} max={100} value={autoMinScore}
                     onChange={(e) => setAutoMinScore(parseInt(e.target.value, 10) || 75)}
                     className="w-20 h-9" data-testid="cm-min-score" />
              <Button onClick={runAutoMap} disabled={autoLoading || !firmId} size="sm" data-testid="cm-auto-btn">
                {autoLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {t('dpd.courierMapping.autoApply')}
              </Button>
            </div>
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="cm-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatBox label={t('dpd.courierMapping.stats.total')} value={data.total_couriers} color="blue" />
            <StatBox label={t('dpd.courierMapping.stats.mapped')} value={data.mapped} color="emerald" />
            <StatBox label={t('dpd.courierMapping.stats.unmapped')} value={data.unmapped} color="orange" />
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[220px]">{t('dpd.courierMapping.cols.courier')}</TableHead>
                  <TableHead className="w-[110px]">{t('dpd.courierMapping.cols.status')}</TableHead>
                  <TableHead>{t('dpd.courierMapping.cols.match')}</TableHead>
                  <TableHead className="w-[280px]">{t('dpd.courierMapping.cols.manual')}</TableHead>
                  <TableHead className="w-[100px] text-right">{t('dpd.courierMapping.cols.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>{Array.from({ length: 5 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                  ))}</TableRow>
                ))}
                {!loading && data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    {t('dpd.courierMapping.empty')}
                  </TableCell></TableRow>
                )}
                {!loading && data.rows.map((r) => (
                  <TableRow key={r.courier_name} data-testid={`cm-row-${r.courier_name}`}>
                    <TableCell className="font-medium">{r.courier_name}</TableCell>
                    <TableCell>
                      {r.status === 'mapped' ? (
                        <Badge className="bg-emerald-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" />{t('dpd.courierMapping.statusMapped')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-700 border-orange-300">
                          <XCircle className="h-3 w-3 mr-1" />{t('dpd.courierMapping.statusUnmapped')}
                        </Badge>
                      )}
                      {r.status === 'mapped' && (
                        <div className="text-[10px] text-gray-500 mt-1">
                          {r.mapping_type} · %{r.confidence}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.status === 'mapped' ? (
                        <div className="flex items-center gap-1">
                          <LinkIcon className="h-3 w-3 text-emerald-600" />
                          <span className="font-medium">{r.employee_name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {r.suggestions.length === 0 && (
                            <span className="text-[11px] text-gray-500">{t('dpd.courierMapping.noSuggestion')}</span>
                          )}
                          {r.suggestions.map((s) => (
                            <button
                              key={s.employee_id}
                              type="button"
                              onClick={() => applyMapping(r.courier_name, s.employee_id, 'manual')}
                              className="text-[11px] px-2 py-0.5 border rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-1"
                              data-testid={`cm-suggest-${s.employee_id}`}
                            >
                              <span>{s.employee_name}</span>
                              <Badge variant="secondary" className="text-[9px]">%{s.score}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.status !== 'mapped' && (
                        <EmployeeCombobox
                          employees={employees}
                          courierName={r.courier_name}
                          onSelect={(empId) => applyMapping(r.courier_name, empId, 'manual')}
                          t={t}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === 'mapped' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeMapping(r.courier_name)}
                          className="text-red-600 hover:text-red-700 h-7 px-2"
                          data-testid={`cm-remove-${r.courier_name}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" />{t('dpd.courierMapping.remove')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="text-[11px] text-gray-500 mt-3">
            {t('dpd.courierMapping.hint', { score: autoMinScore })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function EmployeeCombobox({ employees, courierName, onSelect, t }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          size="sm"
          className="h-8 w-full justify-between text-xs font-normal"
          data-testid={`cm-select-${courierName}`}
        >
          <span className="text-gray-500 truncate">{t('dpd.courierMapping.pickEmployee')}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('dpd.courierMapping.searchPlaceholder')} data-testid="cm-combobox-search" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>{t('dpd.courierMapping.noEmployee')}</CommandEmpty>
            <CommandGroup>
              {employees.map((e) => (
                <CommandItem
                  key={e.id}
                  value={`${e._searchable_name} ${e.email || ''}`}
                  onSelect={() => {
                    setOpen(false);
                    onSelect(e.id);
                  }}
                  className="cursor-pointer text-xs"
                >
                  <span className="font-medium">{e._searchable_name || '(isim yok)'}</span>
                  {e.email && <span className="ml-2 text-[10px] text-gray-500 truncate">{e.email}</span>}
                  {e.role && <span className="ml-auto text-[10px] text-gray-400">{e.role}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function StatBox({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-800',
    emerald: 'bg-emerald-50 text-emerald-800',
    orange: 'bg-orange-50 text-orange-800',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
